import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, BookOpen, Calendar, Download, FolderKanban, Sparkles, ChevronRight } from 'lucide-react';
import FeatureTip from '../../components/FeatureTip';

const Daybooks = () => {
  const [projects, setProjects] = useState([]);
  const [daybooks, setDaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [filterProject, setFilterProject] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, dbRes] = await Promise.all([
        api.getProjects(),
        api.getDaybooks(),
      ]);
      setProjects(projRes.data || []);
      setDaybooks(dbRes.data || []);
    } catch (e) {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedDate) {
      toast.error('Välj projekt och datum');
      return;
    }
    setGenerating(true);
    try {
      await api.generateDaybook({ project_id: selectedProject, date: selectedDate });
      toast.success('Dagbok genererad!');
      setDialogOpen(false);
      setSelectedProject('');
      await fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte generera dagbok');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (db) => {
    try {
      const res = await api.getDaybook(db.id);
      // Trigger PDF download via direct URL
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/daybooks/${db.id}/pdf`;
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Kunde inte ladda ner PDF');
    }
  };

  const projectName = (id) => projects.find(p => p.id === id)?.name || 'Okänt projekt';

  const filtered = daybooks.filter(d => filterProject === 'all' || d.project_id === filterProject);

  const previewDaybook = previewId ? daybooks.find(d => d.id === previewId) : null;

  return (
    <div className="space-y-5" data-testid="daybooks-page">
      <FeatureTip tipId="daybooks" title="AI-genererade dagböcker" body="Klicka 'Generera dagbok' så skapar AI:n en daglig sammanställning baserat på tidrapporter, ÄTA och projektnotering. Du kan ladda ner som PDF eller skicka till kunden." />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="filter-project">
              <SelectValue placeholder="Alla projekt" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla projekt</SelectItem>
              {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? 'dagbok' : 'dagböcker'}</span>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="generate-daybook-btn" className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-9 px-5 rounded-full">
              <Sparkles size={14} className="mr-2" /> Generera dagbok
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generera dagbok med AI</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs">Projekt</Label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger data-testid="select-project"><SelectValue placeholder="Välj projekt" /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Datum</Label>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} data-testid="select-date" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={generating} data-testid="generate-submit-btn" className="bg-[#E8613A] hover:bg-[#D4532F] text-white">
                  {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Genererar...</> : 'Generera'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#E8613A] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="daybooks-empty">
          <BookOpen size={32} className="text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground">Inga dagböcker än</p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-sm mx-auto">
            Klicka på <strong>Generera dagbok</strong> för att låta AI:n skapa en daglig sammanställning från tidrapporter och ÄTA.
          </p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Datum</th>
                <th className="text-left px-5 py-3 font-medium">Projekt</th>
                <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Sammanfattning</th>
                <th className="text-right px-5 py-3 font-medium">Åtgärd</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(db => {
                const dateStr = db.date ? new Date(db.date).toLocaleDateString('sv-SE') : '—';
                const preview = (db.content || '').replace(/[#*_`>]/g, '').replace(/\n+/g, ' ').slice(0, 90);
                const s = db.sources;
                const sourceCount = s ? (s.time_reports + s.machine_hours + s.eta + s.material_events + s.photos) : null;
                return (
                  <tr key={db.id} className="border-t border-border hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => setPreviewId(db.id)} data-testid={`daybook-row-${db.id}`}>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-foreground">
                        <Calendar size={13} className="text-[#E8613A]" />
                        <span className="font-medium">{dateStr}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2 text-foreground">
                        <FolderKanban size={13} className="text-muted-foreground" />
                        <span>{projectName(db.project_id)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-muted-foreground hidden md:table-cell max-w-md">
                      <p className="line-clamp-1">{preview || '—'}</p>
                      {s && sourceCount > 0 && (
                        <p className="text-[10px] text-muted-foreground/70 mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5">
                          <span title="Källor som AI:n använde för dagboken — inget hittas på utöver dessa">
                            Källor:
                          </span>
                          {s.time_reports > 0 && <span>{s.time_reports} tidrapport{s.time_reports === 1 ? '' : 'er'}</span>}
                          {s.machine_hours > 0 && <span>{s.machine_hours} maskin</span>}
                          {s.eta > 0 && <span>{s.eta} ÄTA</span>}
                          {s.material_events > 0 && <span>{s.material_events} material</span>}
                          {s.photos > 0 && <span>{s.photos} foton</span>}
                          {s.progress_milestone && <span className="text-[#E8613A] font-medium">milstolpe {s.progress_milestone.from}→{s.progress_milestone.to}%</span>}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline" size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDownload(db); }}
                          className="h-8 px-3 text-xs"
                          data-testid={`download-daybook-${db.id}`}
                        >
                          <Download size={12} className="mr-1.5" /> PDF
                        </Button>
                        <ChevronRight size={14} className="text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewId} onOpenChange={(v) => { if (!v) setPreviewId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {previewDaybook && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen size={16} className="text-[#E8613A]" />
                  Dagbok {previewDaybook.date ? new Date(previewDaybook.date).toLocaleDateString('sv-SE') : ''}
                </DialogTitle>
              </DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">{projectName(previewDaybook.project_id)}</p>
              {previewDaybook.sources && (
                <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3" data-testid="daybook-sources">
                  <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1.5">Underlag (AI:n hittar inte på något utanför detta)</p>
                  <ul className="text-xs text-amber-900 dark:text-amber-200 space-y-0.5">
                    {previewDaybook.sources.time_reports > 0 && <li>• {previewDaybook.sources.time_reports} tidrapport{previewDaybook.sources.time_reports === 1 ? '' : 'er'}</li>}
                    {previewDaybook.sources.machine_hours > 0 && <li>• {previewDaybook.sources.machine_hours} maskin­timme-post{previewDaybook.sources.machine_hours === 1 ? '' : 'er'}</li>}
                    {previewDaybook.sources.eta > 0 && <li>• {previewDaybook.sources.eta} ÄTA-rapport{previewDaybook.sources.eta === 1 ? '' : 'er'}</li>}
                    {previewDaybook.sources.material_events > 0 && <li>• {previewDaybook.sources.material_events} material­händelse{previewDaybook.sources.material_events === 1 ? '' : 'r'}</li>}
                    {previewDaybook.sources.photos > 0 && <li>• {previewDaybook.sources.photos} foton</li>}
                    {previewDaybook.sources.progress_milestone && <li>• Milstolpe: {previewDaybook.sources.progress_milestone.from}% → {previewDaybook.sources.progress_milestone.to}%</li>}
                  </ul>
                </div>
              )}
              <div className="mt-4 prose prose-sm dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground leading-relaxed bg-muted/30 rounded-lg p-4 border border-border">{previewDaybook.content || 'Tom dagbok'}</pre>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPreviewId(null)}>Stäng</Button>
                <Button onClick={() => handleDownload(previewDaybook)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white">
                  <Download size={14} className="mr-1.5" /> Ladda ner PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Daybooks;
